import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/public-filter-constants';
import { Collection } from '@msw/data';
import z from 'zod';

const CampaignSchema = z.object({
  id: z.string(),
  workspace_id: z.string(),
  campaign_name: z.string(),
  campaign_start_date: z.string(),
  campaign_end_date: z.string().nullable().optional(),
  keywords: z.array(z.string()).optional(),
  hashtags: z.array(z.string()).optional(),
  mentions: z.array(z.string()).optional(),
  creator_count: z.number().int().nonnegative(),
  post_count: z.number().int().nonnegative(),
  tracked_item_count: z.number().int().nonnegative(),
  platforms: z.array(z.enum(SOURCE_PLATFORM_TYPE)),
  creator_preview_avatars: z
    .array(
      z.object({
        name: z.string(),
        profile_image_url: z.string().optional(),
      }),
    )
    .optional(),
  status: z.enum(['active', 'ended', 'scheduled']),
  created_at: z.string(),
  updated_at: z.string(),
  last_collected_at: z.string().nullable().optional(),
});

export const campaignCollection = new Collection({
  schema: CampaignSchema,
});

type MockCampaignRecord = z.infer<typeof CampaignSchema>;

const mockCampaignSeed: MockCampaignRecord[] = [
  {
    id: 'campaign-active-creator-only-seeding',
    workspace_id: '1',
    campaign_name: 'Creator Whitelist Only',
    campaign_start_date: '2026-04-04',
    campaign_end_date: '2026-04-22',
    keywords: [],
    hashtags: [],
    mentions: [],
    creator_count: 14,
    post_count: 0,
    tracked_item_count: 14,
    platforms: [SOURCE_PLATFORM_TYPE.INSTAGRAM, SOURCE_PLATFORM_TYPE.TIKTOK],
    creator_preview_avatars: [
      { name: 'Hannah Cole', profile_image_url: 'https://i.pravatar.cc/80?img=6' },
      { name: 'Yuna Lee', profile_image_url: 'https://i.pravatar.cc/80?img=39' },
      { name: 'Aria West', profile_image_url: 'https://i.pravatar.cc/80?img=52' },
    ],
    status: 'active' as const,
    created_at: '2026-04-03T02:00:00.000Z',
    updated_at: '2026-04-06T02:55:00.000Z',
    last_collected_at: '2026-04-06T02:40:00.000Z',
  },
  {
    id: 'campaign-active-post-url-only-drop',
    workspace_id: '1',
    campaign_name: 'Direct Post URL Drop',
    campaign_start_date: '2026-04-02',
    campaign_end_date: '2026-04-16',
    keywords: [],
    hashtags: [],
    mentions: [],
    creator_count: 6,
    post_count: 11,
    tracked_item_count: 11,
    platforms: [SOURCE_PLATFORM_TYPE.INSTAGRAM, SOURCE_PLATFORM_TYPE.YOUTUBE],
    creator_preview_avatars: [
      { name: 'Sera Moon', profile_image_url: 'https://i.pravatar.cc/80?img=26' },
      { name: 'Daniel Park', profile_image_url: 'https://i.pravatar.cc/80?img=30' },
      { name: 'Lena Choi', profile_image_url: 'https://i.pravatar.cc/80?img=33' },
    ],
    status: 'active' as const,
    created_at: '2026-04-02T01:20:00.000Z',
    updated_at: '2026-04-06T02:35:00.000Z',
    last_collected_at: '2026-04-06T02:10:00.000Z',
  },
  {
    id: 'campaign-scheduled-full-signal-mix',
    workspace_id: '1',
    campaign_name: 'Full Signal Mix Launch',
    campaign_start_date: '2026-04-12',
    campaign_end_date: '2026-05-03',
    keywords: ['launch week'],
    hashtags: ['unboxing'],
    mentions: ['syncly'],
    creator_count: 9,
    post_count: 6,
    tracked_item_count: 15,
    platforms: [
      SOURCE_PLATFORM_TYPE.INSTAGRAM,
      SOURCE_PLATFORM_TYPE.TIKTOK,
      SOURCE_PLATFORM_TYPE.YOUTUBE,
    ],
    creator_preview_avatars: [
      { name: 'Mila Torres', profile_image_url: 'https://i.pravatar.cc/80?img=41' },
      { name: 'Jisoo Han', profile_image_url: 'https://i.pravatar.cc/80?img=54' },
      { name: 'Ethan Cruz', profile_image_url: 'https://i.pravatar.cc/80?img=59' },
    ],
    status: 'scheduled' as const,
    created_at: '2026-04-05T04:20:00.000Z',
    updated_at: '2026-04-06T02:20:00.000Z',
    last_collected_at: null,
  },
  {
    id: 'campaign-active-spring-launch',
    workspace_id: '1',
    campaign_name: 'Spring Launch US',
    campaign_start_date: '2026-04-01',
    campaign_end_date: '2026-04-30',
    keywords: ['spring drop'],
    hashtags: ['springlaunch'],
    mentions: ['syncly'],
    creator_count: 18,
    post_count: 142,
    tracked_item_count: 160,
    platforms: [
      SOURCE_PLATFORM_TYPE.INSTAGRAM,
      SOURCE_PLATFORM_TYPE.TIKTOK,
      SOURCE_PLATFORM_TYPE.YOUTUBE,
    ],
    creator_preview_avatars: [
      { name: 'Ava Brooks', profile_image_url: 'https://i.pravatar.cc/80?img=12' },
      { name: 'Mia Carter', profile_image_url: 'https://i.pravatar.cc/80?img=32' },
      { name: 'Sophie Kim', profile_image_url: 'https://i.pravatar.cc/80?img=45' },
    ],
    status: 'active' as const,
    created_at: '2026-03-28T05:00:00.000Z',
    updated_at: '2026-04-06T01:10:00.000Z',
    last_collected_at: '2026-04-06T00:58:00.000Z',
  },
  {
    id: 'campaign-scheduled-mothers-day',
    workspace_id: '1',
    campaign_name: "Mother's Day Creator Push",
    campaign_start_date: '2026-04-10',
    campaign_end_date: '2026-05-08',
    keywords: ['mothers day'],
    hashtags: ['giftguide'],
    mentions: [],
    creator_count: 12,
    post_count: 0,
    tracked_item_count: 27,
    platforms: [SOURCE_PLATFORM_TYPE.INSTAGRAM, SOURCE_PLATFORM_TYPE.TIKTOK],
    creator_preview_avatars: [
      { name: 'Emma Reed', profile_image_url: 'https://i.pravatar.cc/80?img=24' },
      { name: 'Olivia Park' },
      { name: 'Lila Stone', profile_image_url: 'https://i.pravatar.cc/80?img=28' },
    ],
    status: 'scheduled' as const,
    created_at: '2026-04-02T03:30:00.000Z',
    updated_at: '2026-04-05T06:45:00.000Z',
    last_collected_at: null,
  },
  {
    id: 'campaign-active-tiktok-seeding',
    workspace_id: '1',
    campaign_name: 'TikTok Seeding Q2',
    campaign_start_date: '2026-03-20',
    campaign_end_date: '2026-04-12',
    keywords: ['q2 seeding'],
    hashtags: ['foryou'],
    mentions: [],
    creator_count: 24,
    post_count: 67,
    tracked_item_count: 88,
    platforms: [SOURCE_PLATFORM_TYPE.TIKTOK],
    creator_preview_avatars: [
      { name: 'Noah Bell', profile_image_url: 'https://i.pravatar.cc/80?img=18' },
      { name: 'Jaden Lee', profile_image_url: 'https://i.pravatar.cc/80?img=68' },
      { name: 'Chris Young' },
    ],
    status: 'active' as const,
    created_at: '2026-03-18T02:10:00.000Z',
    updated_at: '2026-04-05T23:20:00.000Z',
    last_collected_at: '2026-04-05T23:05:00.000Z',
  },
  {
    id: 'campaign-active-evergreen-ambassador',
    workspace_id: '1',
    campaign_name: 'Evergreen Ambassador Program',
    campaign_start_date: '2026-03-05',
    campaign_end_date: null,
    keywords: ['ambassador'],
    hashtags: ['alwayson'],
    mentions: [],
    creator_count: 15,
    post_count: 84,
    tracked_item_count: 112,
    platforms: [SOURCE_PLATFORM_TYPE.INSTAGRAM, SOURCE_PLATFORM_TYPE.TIKTOK],
    creator_preview_avatars: [
      { name: 'Grace Miller', profile_image_url: 'https://i.pravatar.cc/80?img=10' },
      { name: 'Zoe Park', profile_image_url: 'https://i.pravatar.cc/80?img=49' },
      { name: 'Nina Park' },
    ],
    status: 'active' as const,
    created_at: '2026-03-01T04:10:00.000Z',
    updated_at: '2026-04-04T10:15:00.000Z',
    last_collected_at: '2026-04-04T10:10:00.000Z',
  },
  {
    id: 'campaign-ended-holiday-recap',
    workspace_id: '1',
    campaign_name: 'Holiday Recap 2025',
    campaign_start_date: '2025-11-20',
    campaign_end_date: '2025-12-31',
    keywords: ['holiday sale'],
    hashtags: ['giftideas'],
    mentions: [],
    creator_count: 31,
    post_count: 296,
    tracked_item_count: 332,
    platforms: [SOURCE_PLATFORM_TYPE.INSTAGRAM, SOURCE_PLATFORM_TYPE.YOUTUBE],
    creator_preview_avatars: [
      { name: 'Ella Hart', profile_image_url: 'https://i.pravatar.cc/80?img=15' },
      { name: 'Mason Scott', profile_image_url: 'https://i.pravatar.cc/80?img=22' },
      { name: 'Ivy Chen', profile_image_url: 'https://i.pravatar.cc/80?img=36' },
    ],
    status: 'ended' as const,
    created_at: '2025-11-14T08:20:00.000Z',
    updated_at: '2026-01-01T00:20:00.000Z',
    last_collected_at: '2025-12-31T15:00:00.000Z',
  },
  {
    id: 'campaign-ended-evergreen-affiliate',
    workspace_id: '1',
    campaign_name: 'Evergreen Affiliate Test',
    campaign_start_date: '2026-02-01',
    campaign_end_date: '2026-03-01',
    keywords: ['affiliate'],
    hashtags: ['dailydeal'],
    mentions: [],
    creator_count: 9,
    post_count: 51,
    tracked_item_count: 64,
    platforms: [SOURCE_PLATFORM_TYPE.INSTAGRAM],
    creator_preview_avatars: [
      { name: 'Leo Adams', profile_image_url: 'https://i.pravatar.cc/80?img=20' },
      { name: 'Ruby Seo' },
    ],
    status: 'ended' as const,
    created_at: '2026-01-29T05:20:00.000Z',
    updated_at: '2026-03-01T09:00:00.000Z',
    last_collected_at: '2026-03-01T08:40:00.000Z',
  },
];

export async function initMockCampaignDB() {
  campaignCollection.clear();
  for (const campaign of mockCampaignSeed) {
    await campaignCollection.create(campaign);
  }
}
